'use client';

import { sendGTMEvent } from '@next/third-parties/google';
import { FacebookShareButton, LinkedinShareButton, TwitterShareButton } from 'next-share';
import { usePathname } from 'next/navigation';
import { useState } from 'react';

import { cn } from '@natu/utils/cn';
import { getEventPayload } from '@natu/utils/getEventPayload';
import { usePageContext } from '@natu/utils/PageContextProvider';

import { ShareIcon } from './ShareIcon';

export interface ShareProps {
  className?: string;
}

export const Share = ({ className }: ShareProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const pathname = usePathname();
  const { isCampaign } = usePageContext() || {};
  const eventsData = {
    copy: {
      event: 'E_click_copy_article_link',
      payload: getEventPayload({ pathname, event: 'E_click_copy_article_link', isCampaign }),
    },
    facebook: {
      event: 'E_click_share_facebook',
      payload: getEventPayload({ pathname, event: 'E_click_share_facebook', isCampaign }),
    },
    twitter: {
      event: 'E_click_share_x',
      payload: getEventPayload({ pathname, event: 'E_click_share_x', isCampaign }),
    },
    linkedin: {
      event: 'E_click_share_linkedin',
      payload: getEventPayload({ pathname, event: 'E_click_share_linkedin', isCampaign }),
    },
  };

  const shareLink = `https://naturaily.com${pathname}`;
  const hashtag = 'naturaily';

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(shareLink);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 1500);
    sendGTMEvent({ event: eventsData.copy.event, ...eventsData.copy.payload });
  };

  return (
    <div className={cn('flex items-center gap-4', className)}>
      <button onClick={handleCopyToClipboard} aria-label="Copy to clipboard">
        <ShareIcon icon={isCopied ? 'check' : 'link'} />
      </button>
      <FacebookShareButton
        url={shareLink}
        hashtag={`#${hashtag}`}
        onClick={() =>
          sendGTMEvent({ event: eventsData.facebook.event, ...eventsData.facebook.payload })
        }
      >
        <ShareIcon icon="facebook" />
      </FacebookShareButton>
      <TwitterShareButton
        url={shareLink}
        hashtags={[hashtag]}
        onClick={() =>
          sendGTMEvent({ event: eventsData.twitter.event, ...eventsData.twitter.payload })
        }
      >
        <ShareIcon icon="twitter" />
      </TwitterShareButton>
      <LinkedinShareButton
        url={shareLink}
        onClick={() =>
          sendGTMEvent({ event: eventsData.linkedin.event, ...eventsData.linkedin.payload })
        }
      >
        <ShareIcon icon="linkedin" />
      </LinkedinShareButton>
    </div>
  );
};
